import _ from 'lodash';
import Link from '../../ui/Link';
import reporter from '../../privates/reporter';
import * as pagesDataManipulation from '../../privates/utils/pagesDataManipulation';
import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';
import platformUtils from '../../privates/platformUtils';
import linkGenerator from '../../privates/utils/linkGenerator';
import {isNilOrEmptyString, emptyStringIfNil} from '../../privates/utils';

/**
 * @mixin LinkableMixin
 * @summary Provides functionality for elements that can act as links.
 * @description
 *  `LinkableMixin` is a mixin that provides basic properties for elements on the page that can act as links.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  let url = $w("#LinkableMixin").link;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `LinkableMixin`. 
 * 
 *   ```javascript
 *   let url = $w("#myElement").link;
 *   ```
 *  
 * @memberof $w
 */
class LinkableMixin {

    /**
     * @member link
     * @label property
     * @syntax
     * get link(): string
     * set link(value: string): void
     * @summary Sets or gets the element's link.
     * @description
     *  When the element is clicked, the browser navigates to the `link` location.
     *
     *  The different types of links you can use are:
     *
     *  + `/localPageURL`: another page on your site
     *  + `/`: your site's home page
     *  + `http(s)://<url>`: an external web address
     *  + `wix:document://<location>`: a document stored in the Media Manager
     *  + `mailto:<email-address>?subject=<subject>`: an email
     *  + `tel:<phone number>`: a phone number
     *
     *
     *  The `link` property attempts to properly encode the URL value that
     *  you set. For example, `.../some page` is encoded to `.../some%20page`.
     *  However, some URLs do not have one unambiguous encoding. In those cases
     *  it is up to you to encode the URL to reflect your intentions. Because of
     *  these situations, it is a best practice to always encode URLs before you
     *  set them as the value of the `link` property. Regardless of how you set
     *  the `link` property, when you get its value, it returns an encoded URL.
     *
     *  Note that Wix URLs do not contain spaces. A page which has spaces in its
     *  name has its spaces replaced with dashes (`-`). Similarly,
     *  a [dynamic page URL](https://support.wix.com/en/article/creating-a-unique-dynamic-page-url) which 
     *  contains the value of a field in your collection with spaces
     *  has its spaces replaced with dashes (`-`).
     * @type {external:String}
     * @default An empty string
     * @snippet [LinkableMixin-link_get.es6=Get the link URL from an element]
     * @snippet [LinkableMixin-link_set_local.es6=Set an element to open a local link]
     * @snippet [LinkableMixin-link_set_weblink.es6=Set an element to open an external web link]
     * @snippet [LinkableMixin-link_set_weblink_new_page.es6=Set an element to open an external web link in a new browser page when clicked]
     * @snippet [LinkableMixin-link_set_mailto.es6=Set an element to open a new email window when clicked]
     * @snippet [LinkableMixin-link_set_document.es6=Set an element to open a document]
     * @snippet [LinkableMixin-link_set_telephone.es6=Set an element to make a phone call when clicked]
     * @see [target](#target)
     * @memberof $w.LinkableMixin
     * @attr {external:String}
     * @attr {external:String} target
     */
    get link() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const link = RMI.getData(compId).link;
        return emptyStringIfNil(link && (link.link || new Link(link).getValue()));
    }

    /**
     * @member link
     * @param {external:String} value
     * @memberof $w.LinkableMixin
     */
    set link(value) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;

        const isValid = validate({
            compType,
            functionName: 'link',
            dataToValidate: {link: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }

        if (!isNilOrEmptyString(value)) {
            const linkObject = linkGenerator.convertLinkToModelFormatAndReportIfError(value, this.target, 'link');

            if (!linkObject) {
                return;
            }

            if (linkObject && linkObject.pageId === '#masterPage' && linkObject.queryParams) {
                reporter.reportSdkWarning('Same page Anchor link can not accept query Params');
                delete linkObject.queryParams;
            }
            RMI.setData(compId, {
                link: linkObject
            });
        } else {
            RMI.setData(compId, {link: null});
        }
    }

    /**
     * @member target
     * @label property
     * @syntax
     * get target(): string
     * set target(value: TargetType): void
     * type TargetType = "_blank" | "_self"
     * @summary Sets or gets the target of the element's link.
     * @description
     *  An element's `target` determines where its [link](#link) opens. The
     *  target can be set so the [link](#link) opens in the current page or
     *  in a new page.
     *
     *  Supported values are:
     *
     *  + `"_blank"`: The link opens in a new tab or window.
     *  + `"_self"`: The link opens in the tab or window.
     *
     * 
     * > **Note:**  Link targets are only supported for external web links
     *  (e.g. `http://www.wix.com`).
     * @type {external:String}
     * @default "_blank"
     * @snippet [LinkableMixin-target_get.es6=Get the link target from an element]
     * @snippet [LinkableMixin-target_set.es6=Set the link target for an element]
     * @snippet [LinkableMixin-link_set_weblink_new_page.es6=Set an element to open an external web link in a new browser page when clicked]
     * @see [link](#link)
     * @memberof $w.LinkableMixin
     * @attr {external:String} target
     */
    get target() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const link = RMI.getData(compId).link;

        return link && link.target ? link.target : '_blank';
    }

  /**
   * @member target
   * @memberof $w.LinkableMixin
   * @param {external:String} target
   */
    set target(target) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'target',
            dataToValidate: {target},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const RGI = platformUtils.getRGI();
        const compId = members._compId.get(this);
        let link = RMI.getData(compId).link;

        if (!link) {
            const siteStructure = RMI.getSiteStructure() || {};
            const navigation = RGI.getNavigationInfo();
            const pageData = RMI.getPageData();
            const currentPageSiteStructureData = _.find(siteStructure.pages, {id: navigation.currentPageId});
            const currentPageUri = pagesDataManipulation.getPageURI(currentPageSiteStructureData, pageData.currentNavigationInfo);

            link = linkGenerator.convertLinkToModelFormatAndReportIfError(currentPageUri, this.target, 'target');
            if (!link) {
                return;
            }

        }
        link.target = target;
        RMI.setData(compId, {link});
    }
}

export default LinkableMixin;
